/**
 * Unified Auth Gate Component
 * Integrates with Convex creatorAuth for secure authentication
 */

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Fingerprint,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  CheckCircle,
  Smartphone,
  RefreshCw,
  LogIn,
  Crown,
} from "lucide-react";
import { authService } from "@/services/authService";
import { convexService } from "@/services/convexService";
import type { AuthLevel } from "@/types";

interface unifiedAuthGateProps {
  onAuthSuccess: (level: AuthLevel) => void;
  children?: React.ReactNode;
}

/**
 * Rate limiter for auth attempts
 */
class AuthRateLimiter {
  private attempts: number[] = [];
  private readonly MAX_ATTEMPTS = 5;
  private readonly LOCKOUT_DURATION = 30 * 1000; // 30 seconds

  recordAttempt(): { blocked: boolean; waitTime?: number } {
    const now = Date.now();

    // Remove old attempts (outside lockout window)
    this.attempts = this.attempts.filter(t => now - t > this.LOCKOUT_DURATION);

    if (this.attempts.length >= this.MAX_ATTEMPTS) {
      const oldestNowBlocked = this.attempts[0];
      const waitTime = this.LOCKOUT_DURATION - (now - oldestNowBlocked);
      return { blocked: true, waitTime };
    }

    this.attempts.push(now);
    return { blocked: false };
  }

  reset(): void {
    this.attempts = [];
  }

  getRemainingAttempts(): number {
    const now = Date.now();
    this.attempts = this.attempts.filter(t => now - t > this.LOCKOUT_DURATION);
    return this.MAX_ATTEMPTS - this.attempts.length;
  }
}

const rateLimiter = new AuthRateLimiter();

export function UnifiedAuthGate({ onAuthSuccess, children }: unifiedAuthGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLevel, setAuthLevel] = useState<AuthLevel>("none");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password auth state
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Passkey auth state
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  // Convex auth state
  const [useConvexAuth, setUseConvexAuth] = useState(false);
  const [convexAvailable, setConvexAvailable] = useState(false);

  // Rate limiting
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutEnd, setLockoutEnd] = useState<number | null>(null);

  // Initialize
  useEffect(() => {
    // Check passkey support
    setIsPasskeySupported(
      window.PublicKeyCredential !== undefined &&
      typeof window.PublicKeyCredential === "function"
    );

    // Check Convex availability
    const checkConvex = async () => {
      const convexUrl = (import.meta.env as any)['VITE_CONVEX_URL'] || "";
      if (convexUrl) {
        convexService.initialize(convexUrl);
        const available = await convexService.healthCheck();
        setConvexAvailable(available);
        if (available) {
          authService.setConvexUrl(convexUrl);
        }
      }
    };

    checkConvex();

    // Check if already authenticated
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        const session = authService.getSession();
        if (session) {
          setIsAuthenticated(true);
          setAuthLevel(session.level);
          onAuthSuccess(session.level);
        }
      }
    };

    checkAuth();
  }, [onAuthSuccess]);

  // Update rate lock
  useEffect(() => {
    if (!isLocked) return;

    const interval = setInterval(() => {
      if (lockoutEnd && Date.now() < lockoutEnd) {
        const waitTime = Math.ceil((lockoutEnd - Date.now()) / 1000);
        setError(`Too many attempts. Wait ${waitTime} seconds.`);
      } else {
        setIsLocked(false);
        setLockoutEnd(null);
        setError(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLocked, lockoutEnd]);

  // Handle password login
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLocked) return;
    if (!password.trim()) return;

    const { blocked, waitTime } = rateLimiter.recordAttempt();
    if (blocked) {
      setIsLocked(true);
      setLockoutEnd(Date.now() + (waitTime || 30000));
      if (waitTime) {
        setError(`Too many attempts. Wait ${Math.ceil(waitTime / 1000)} seconds.`);
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use authService for password auth (with Convex support if enabled)
      const session = useConvexAuth && convexAvailable
        ? await authService.loginTwoFactor(password, true)
        : await authService.loginPassword(password);

      // Auth successful
      setIsAuthenticated(true);
      setAuthLevel(session.level);
      onAuthSuccess(session.level);
      setPassword("");
    } catch (err) {
      const remaining = rateLimiter.getRemainingAttempts();
      const message = err instanceof Error ? err.message : "Authentication failed";
      setError(`${message} (${remaining} attempts remaining)`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle passkey login
  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true);
    setError(null);

    try {
      const session = await authService.loginPasskey();

      setIsAuthenticated(true);
      setAuthLevel(session.level);
      onAuthSuccess(session.level);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey authentication failed");
    } finally {
      setIsPasskeyLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setAuthLevel("none");
    setPassword("");
  };

  // Authenticated state - render children with status bar
  if (isAuthenticated) {
    return (
      <>
        {/* Auth Status Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-2"
        >
          <div className="flex items-center gap-3">
            <div className={cn(
              "flex h-10 w-10 items-center justify-center rounded-lg",
              authLevel === "creator"
                ? "bg-purple-500/20 glow-purple"
                : "bg-blue-500/20 glow-blue"
            )}>
              {authLevel === "creator" ? (
                <Crown className="h-5 w-5 text-purple-400" />
              ) : (
                <Shield className="h-5 w-5 text-blue-400" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[hsl(var(--foreground))]">
                  {authLevel === "creator" ? "Creator" : "Master"} Access
                </span>
                <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs text-green-400">
                  Active
                </span>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Authenticated via {authLevel === "creator" ? "Passkey" : "Password"}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-lg bg-[hsl(var(--secondary))] px-3 py-1.5 text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]/80 cursor-pointer"
          >
            Logout
          </button>
        </motion.div>
        {children}
      </>
    );
  }

  const remainingAttempts = rateLimiter.getRemainingAttempts();
  const lockoutWaitSeconds = lockoutEnd
    ? Math.max(0, Math.ceil((lockoutEnd - Date.now()) / 1000))
    : 0;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--background))] p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-xl flex flex-col gap-4"
      >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--card))]">
            <Shield className="h-6 w-6 text-[hsl(var(--primary))]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">
              Authentication Required
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {useConvexAuth && convexAvailable
                ? "Two-factor authentication via Convex"
                : "Secure access required"}
            </p>
          </div>
        </div>

        {/* Auth Mode Toggle */}
        {convexAvailable && (
          <button
            onClick={() => {
              setUseConvexAuth(!useConvexAuth);
              setError(null);
              rateLimiter.reset();
              setIsLocked(false);
            }}
            className={cn(
              "relative flex h-8 w-12 items-center rounded-full transition-colors cursor-pointer",
              useConvexAuth ? "bg-purple-500" : "bg-[hsl(var(--secondary))]"
            )}
          >
            <motion.div
              animate={{ x: useConvexAuth ? 20 : 4 }}
              className="h-6 w-6 rounded-full bg-white shadow"
            />
          </button>
        )}
      </div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-lg border border-red-500/20 bg-red-500/10 p-3"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <span className="text-sm text-red-400">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-300 cursor-pointer"
              >
                <AlertCircle className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Method Selection */}
      <div className="grid grid-cols-2 gap-3">
        {/* Password */}
        <div className={cn(
          "rounded-xl border p-4 transition-colors",
          !useConvexAuth || isPasskeySupported
            ? "border-[hsl(var(--border))] bg-[hsl(var(--card))]"
            : "hidden"
        )}>
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-5 w-5 text-[hsl(var(--primary))]" />
            <div>
              <h3 className="font-semibold text-[hsl(var(--foreground))]">
                Password Key
              </h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Master or Creator key
              </p>
            </div>
          </div>

          <form onSubmit={handlePasswordLogin} className="space-y-3">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your key..."
                disabled={isLocked || isLoading}
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 pr-9 text-sm text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] cursor-pointer"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {!isLocked && remainingAttempts < 5 && (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Attempts remaining: <span className="text-red-400">{remainingAttempts}</span>
              </p>
            )}

            <button
              type="submit"
              disabled={isLocked || isLoading || !password.trim()}
              className={cn(
                "w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium cursor-pointer transition-opacity",
                "bg-[hsl(var(--primary))] text-black",
                "disabled:opacity-50"
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Login
                </>
              )}
            </button>
          </form>
        </div>

        {/* Passkey / Biometric */}
        <div className={cn(
          "rounded-xl border p-4",
          isPasskeySupported
            ? "border-[hsl(var(--border))] bg-[hsl(var(--card))]"
            : "hidden"
        )}>
          <div className="flex items-center gap-3 mb-4">
            <Fingerprint className="h-5 w-5 text-[hsl(var(--primary))]" />
            <div>
              <h3 className="font-semibold text-[hsl(var(--foreground))]">
                Passkey / Biometric
              </h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Face ID, Touch ID, hardware key
              </p>
            </div>
          </div>

          <button
            onClick={handlePasskeyLogin}
            disabled={isPasskeyLoading}
            className={cn(
              "w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[hsl(var(--border))] p-6 text-sm transition-colors cursor-pointer",
              "hover:border-[hsl(var(--primary))]/50",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {isPasskeyLoading ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" />
                <span className="text-[hsl(var(--foreground))]">Waiting for biometrics...</span>
              </>
            ) : (
              <>
                <Fingerprint className="h-8 w-8 text-[hsl(var(--primary))]" />
                <span className="text-[hsl(var(--foreground))]">Verify with Passkey</span>
                {useConvexAuth && convexAvailable && (
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    Requires password verification first
                  </span>
                )}
              </>
            )}
          </button>
        </div>

        {/* Convex Auth Notice */}
        {useConvexAuth && convexAvailable && (
          <div className="col-span-2 rounded-lg border border-purple-500/30 bg-purple-500/10 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-purple-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-purple-400">Secure Authentication</h4>
                <p className="mt-1 text-sm text-purple-300/70">
                  Two-factor authentication via Convex. Password verification is validated against the creatorAuth system.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auth Info */}
      <div className="rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4">
        <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <ul className="space-y-1">
            <li><strong>Creator Key</strong> - Full access to all features</li>
            <li><strong>Master Key</strong> - Override mode with limited access</li>
            <li><strong>Passkey</strong> - Biometric login using Face ID, Touch ID or hardware key</li>
            {useConvexAuth && convexAvailable && (
              <li><strong>Convex Auth</strong> - Session validated via cloud backend</li>
            )}
          </ul>
        </div>
      </div>
      </motion.div>
    </div>
  );
}

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(" ");
}
